import { LanguageSwitcherModal } from "@modules/Navbar/components/LanguageSwitcherModal";
import { useLocale } from "@modules/shared/hooks/useLocale";
import { countries } from "@modules/shared/localization/const/countries";
import { MappedLocales } from "@modules/shared/localization/const/language";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Button } from "@ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@ui/components/ui/dropdown-menu";
import { cn } from "@ui/lib/utils";
import { setDefaultOptions } from "date-fns";
import Cookies from "js-cookie";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGE_KEY } from "../../../../consts/keys";

type Props = {
  isMobileView?: boolean;
};

export const LanguageSwitcher: React.FC<Props> = ({ isMobileView }) => {
  const { t } = useTranslation("Navbar");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const locale = useLocale();
  const [isOpen, setIsOpen] = useState(false);

  const selectedCountry = countries[locale];

  const handleLanguageChange = (key: keyof typeof countries) => {
    Cookies.set(LANGUAGE_KEY, key);
    setDefaultOptions({
      locale: MappedLocales.get(key),
    });

    const segments = pathname.split("/");
    if (segments.length > 1) {
      segments[1] = key;
    }

    const newPath = segments.join("/") || "/";
    void navigate({ to: newPath as never });
    setIsOpen(false);
  };

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            size="small"
            dark="ghost"
            className={cn("mx-2")}
            aria-label={t("selectYourCountry")}
            aria-expanded={isOpen}
            onClick={() => {
              if (isMobileView) {
                setIsOpen((previous) => !previous);
              }
            }}
          >
            <div className="flex items-center gap-3">
              <p className="md:text-base">
                {t(`countries.${selectedCountry.label}` as never)}
              </p>
              <img
                src={selectedCountry.flagSrc}
                alt=""
                width={24}
                height={24}
                className="h-6 w-6 rounded-full object-cover"
                aria-hidden="true"
              />
            </div>
          </Button>
        </DropdownMenuTrigger>

        {!isMobileView && (
          <DropdownMenuContent
            align="end"
            className="z-100 p-2 mt-2 min-w-55 rounded-xl border-border-5 bg-background-7 text-text-1"
          >
            {Object.entries(countries).map(([key, country]) => (
              <DropdownMenuItem
                key={country.label}
                onSelect={() =>
                  handleLanguageChange(key as keyof typeof countries)
                }
                className="cursor-pointer"
              >
                <div className="flex w-full items-center gap-3">
                  <img
                    src={country.flagSrc}
                    alt=""
                    width={24}
                    height={24}
                    className="h-6 w-6 rounded-full object-cover"
                    loading="lazy"
                    aria-hidden="true"
                  />
                  <p>{t(`countries.${country.label}` as never)}</p>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        )}
      </DropdownMenu>

      {isMobileView && (
        <LanguageSwitcherModal
          showDropdown={isOpen}
          setShowDropdown={setIsOpen}
        />
      )}
    </>
  );
};
